import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import * as React from 'react'

import {stringifyId} from '../../../../types'
import {copyClipboard} from '../../../../utils/clipboard'
import CopyIconButton from '../../../common/CopyIconButton/CopyIconButton'
import Link from '../../../common/Link/Link'
import {RouterButton} from '../../../common/RouterLink/RouterLink'
import {
  generateShowLogParam,
  getFullBuildLogLinkParams,
  getTrimmedMessages,
} from '../BuildLog.utils'
import BuildLogMessages from '../BuildLogMessages/BuildLogMessages'

import type {Props} from './BuildLogPreview.types'

import styles from './BuildLogPreview.css'

function BuildLogPreview({
  buildId,
  problemOccurrenceId,
  focusLine,
  expandLine,
  className,
  messages,
  lastMessageIncluded,
  testLogAnchorPosition,
  showDiffBetweenTestValues,
  loading,
}: Props) {
  const logTailRef = React.useRef<any>(null)
  const copyLog = React.useCallback(async () => {
    await copyClipboard(logTailRef.current?.innerText)
  }, [])

  const filtered = React.useMemo(() => getTrimmedMessages(messages), [messages])
  const renderContent = () => (
    <div ref={logTailRef}>
      <BuildLogMessages
        messages={filtered}
        messageClassName={styles.message}
        minimiseLevels
        breakLines
      />
    </div>
  )

  const logParams = new URLSearchParams(location.search)
  logParams.set(
    'showLog',
    generateShowLogParam(buildId, focusLine, expandLine != null ? [Number(expandLine)] : null),
  )
  return (
    <div className={className}>
      <div>
        <div className={classNames(styles.header, {[styles.hasMessages]: filtered.length > 0})}>
          <div className={styles.heading}>
            <span className={styles.title}>
              {problemOccurrenceId != null
                ? 'Build Log'
                : filtered.length > 0
                  ? 'Stacktrace'
                  : 'No stacktrace'}
              {loading ? <LoaderInline className={styles.loader} /> : null}
            </span>
          </div>
          {showDiffBetweenTestValues && (
            <div className={styles.diff}>
              <Link
                className={styles.diffLink}
                href={`/comparisonFailureDiffView.html?buildId=${stringifyId(buildId)}&testId=${
                  expandLine ?? ''
                }`}
                target="_blank"
                rel="noreferrer"
                relative
              >
                {'Show diff'}
              </Link>
              <span>{'between expected and actual values'}</span>
            </div>
          )}
          {filtered.length > 0 && (
            <div className={styles.actions}>
              <CopyIconButton
                iconClassName={styles.actionButton}
                title={'Copy stacktrace to clipboard'}
                onClick={copyLog}
                beforeCopyContent={
                  <span data-test-copy-stacktrace-text>{'Copy to clipboard'}</span>
                }
                afterCopyContent={
                  <span data-test-copy-stacktrace-text>{'Copied to clipboard'}</span>
                }
              />
            </div>
          )}
        </div>
        {filtered.length > 0 && <div className={styles.content}>{renderContent()}</div>}
      </div>
      {filtered.length > 0 && (
        <>
          {!lastMessageIncluded && (
            <div className={styles.warning}>
              {'Part of the output has been trimmed. You can see the full version in build log.'}
            </div>
          )}
          <div className={styles.footer}>
            <RouterButton
              className={styles.button}
              params={getFullBuildLogLinkParams({
                buildId,
                line: testLogAnchorPosition ?? focusLine,
                expandState: expandLine != null ? [Number(expandLine)] : null,
                logFilter: 'debug',
              })}
            >
              {'Open in build log'}
            </RouterButton>
          </div>
        </>
      )}
    </div>
  )
}

export default BuildLogPreview
